import type { Session, SupabaseClient } from '@supabase/supabase-js'

export type ServerLoginTokens = {
  access_token: string
  refresh_token: string
}

export type HydrateBrowserSessionResult =
  | { status: 'hydrated'; session: Session }
  | { status: 'error'; error: unknown }

/**
 * After POST /api/auth/login sets Http cookies via the server client, the browser
 * Supabase singleton still has no in-memory session and `getSession()` alone is not
 * enough to establish one reliably. `setSession` writes tokens into cookie storage
 * and notifies `onAuthStateChange(SIGNED_IN)`.
 */
export async function hydrateBrowserSessionAfterServerLogin(
  supabase: SupabaseClient,
  tokens: ServerLoginTokens
): Promise<HydrateBrowserSessionResult> {
  if (!tokens.access_token || !tokens.refresh_token) {
    return {
      status: 'error',
      error: new Error('Missing access or refresh token from server login'),
    }
  }

  const { data, error } = await supabase.auth.setSession({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
  })

  if (error || !data.session?.user) {
    return {
      status: 'error',
      error: error ?? new Error('setSession returned no session'),
    }
  }

  return { status: 'hydrated', session: data.session }
}
